import { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import ReactPaginate from 'react-paginate';
import PropTypes from 'prop-types';
import CardSkeleton from 'sealink/react-components/card-skeleton';
import { useGiftCardsQuery } from 'redux/api_slice/gift_cards';
import Card from './card';

const ALL = 'Select all';
const Loading = () => (
  <>
    {[...Array(6)].map((e, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <div className="col-12 col-sm-6 col-lg-4 mb-5" key={index}>
        <CardSkeleton />
      </div>
    ))}
  </>
);
const NoData = ({ hideFilter, onClick }) => (
  <div className="d-flex flex-column align-items-center">
    <p className="fw-bold text-x-large">Sorry, no redeemable products found.</p>
    {!hideFilter && (
      <>
        <p>Try adjusting your filters to find other products.</p>
        <Button variant="outline-primary" onClick={onClick}>
          Reset filters
        </Button>
      </>
    )}
  </div>
);

NoData.propTypes = {
  hideFilter: PropTypes.bool,
  onClick: PropTypes.func,
};

function GiftCards({ state }) {
  const [destination, setDestination] = useState(
    () =>
      state ||
      decodeURIComponent(
        window.location.hash.replace(/^#/, '').replaceAll('-', ' ')
      )
  );
  const [page, setPage] = useState(1);
  const { data = {}, isFetching } = useGiftCardsQuery({
    destinations: [destination],
    page,
  });
  const { destinations = [], entries = [], total } = data || {};
  const noData = total === 0;

  return (
    <div className="gift-cards space-y-4">
      {!state && (
        <>
          <h3 className="text-center mb-4">Filter by destination</h3>

          <Form.Select
            className="gift-cards__select mx-auto"
            value={destination}
            onChange={({ target }) => {
              const selected = target.value === ALL ? '' : target.value;

              window.location.hash = selected.replaceAll(' ', '-');
              setDestination(selected);

              // Always reset to page 1 when changing destination
              setPage(1);
            }}
          >
            {[ALL, ...destinations].map((dest) => (
              <option key={dest} value={dest}>
                {dest}
              </option>
            ))}
          </Form.Select>
        </>
      )}

      <div className="row mt-5">
        {isFetching ? (
          <Loading />
        ) : (
          <>
            {noData ? (
              <NoData
                hideFilter={!!state}
                onClick={() => {
                  window.location.hash = '';
                  setDestination('');
                  setPage(1);
                }}
              />
            ) : (
              <>
                {entries.map((entry) => (
                  <div
                    key={entry.url}
                    className="col-12 col-sm-6 col-lg-4 mb-5 card-wrapper"
                  >
                    <Card
                      link={entry.url}
                      bgImage={entry.previewImage.url}
                      title={entry.title}
                      bodyText={entry.previewText}
                      fromPrice={entry.fromPrice}
                      price={entry.productSalePrice || entry.productPrice}
                      priceDefinition={entry.productPriceDefinition}
                      tenant={entry.tenant}
                      footerText="Redeem now"
                    />
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>

      {!noData && (
        <ReactPaginate
          className="pagination justify-content-center mt-0"
          nextLabel={<i className="fa-solid fa-angle-right" />}
          onPageChange={({ selected }) => setPage(selected + 1)}
          forcePage={page - 1}
          pageRangeDisplayed={3}
          marginPagesDisplayed={2}
          pageCount={Math.ceil(data.total / data.perPage)}
          previousLabel={<i className="fa-solid fa-angle-left" />}
          pageClassName="page-item"
          pageLinkClassName="page-link"
          previousClassName="page-item"
          previousLinkClassName="page-link"
          nextClassName="page-item"
          nextLinkClassName="page-link"
          breakLabel="..."
          breakClassName="page-item"
          breakLinkClassName="page-link"
          containerClassName="pagination"
          activeClassName="active"
          renderOnZeroPageCount={null}
        />
      )}
    </div>
  );
}

export default GiftCards;

GiftCards.propTypes = {
  state: PropTypes.string,
};
